<template>
  <nav ref="root" class="adn-anchor-navigation" data-test-anchor-navigation>
    <ul ref="container" class="adn-anchor-navigation__list container">
      <li
        v-for="anchor in anchors"
        :key="anchor.selector"
        class="adn-anchor-navigation__list-item"
        data-test-anchor-navigation-list-item
      >
        <a
          :href="anchor.selector"
          class="adn-anchor-navigation__link"
          data-test-anchor-navigation-link
          @click.prevent="scrollTo(anchor.selector, $event)"
        >
          {{ anchor.label }}
        </a>
      </li>
    </ul>
  </nav>
</template>

<script>
import VueScrollTo from 'vue-scrollto'

export default {
  name: 'AdnAnchorNavigation',
  props: {
    anchors: {
      type: Array,
      required: true,
      default: () => [],
    },
    topPosition: {
      type: Number,
      required: false,
      default: 0,
    },
    useNative: {
      type: Boolean,
      default: false,
    },
    verticalOffset: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      observer: { disconnect() {} },
      offset: NaN,
      lastAnchorId: undefined,
      isVerticalScroll: false,
      horizontalScrollDuration: 300,
      verticalScrollDuration: 500,
      verticalScrollEventListener: null,
    }
  },
  watch: {
    topPosition() {
      this.onResize()
    },
  },
  mounted() {
    this.onResize()
    this.verticalScrollEventListener = this.debounce(this.highlightMenuItemsOnSrollEvent, 50)
    this.$refs.root.addEventListener('transitionend', this.onResize)
    window.addEventListener('resize', this.onResize, { passive: true })
    window.addEventListener('scroll', this.verticalScrollEventListener)
  },
  beforeUnmount() {
    this.$refs.root.removeEventListener('transitionend', this.onResize)
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('scroll', this.verticalScrollEventListener)
    this.observer.disconnect()
  },
  methods: {
    debounce(callback, delay = 250) {
      let timeoutId
      return (...args) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => {
          timeoutId = null
          callback(...args)
        }, delay)
      }
    },
    onResize() {
      const newOffset = -parseInt(getComputedStyle(this.$refs.root).top, 10) || 0
      if (newOffset !== this.offset) {
        this.offset = newOffset
        // sadly, we won't see a :stuck pseudo-class until they figure out https://github.com/w3c/csswg-drafts/issues/1656
        this.observer.disconnect()
        this.observer = new IntersectionObserver(
          ([e]) => e.target.classList.toggle('stuck', e.intersectionRect.y === -this.offset + 1),
          { threshold: 1, rootMargin: `${this.offset - 1}px 1000px 1000px 1000px` },
        )
        this.observer.observe(this.$refs.root)
      }
    },
    scrollTo(selector, event) {
      if (this.useNative) {
        const el = document.querySelector(selector)
        const elTop = el.offsetTop
        window.parent.scroll({ left: 0, top: elTop + this.verticalOffset, behavior: 'smooth' })
      } else {
        VueScrollTo.scrollTo(selector, this.verticalScrollDuration, {
          offset: this.offset - 60,
          onStart: () => {
            this.isVerticalScroll = true
          },
          onDone: () => {
            this.isVerticalScroll = false
          },
        })
      }
      if (event) event.target?.blur()
    },
    highlightMenuItemsOnSrollEvent() {
      // DEV NOTE : use intersectionObserver when the feature is fully ready in all browsers
      const menu = this.$el
      const menuHeight = menu.offsetHeight + 1
      const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)
      const menuEntries = {}
      Array.from(menu.querySelectorAll('a')).forEach((entry) => {
        const anchorId = entry.getAttribute('href')
        menuEntries[anchorId] = entry
      })
      const pageAnchors = Array.from(menu.querySelectorAll('a')).map((entry) => {
        const anchorId = entry.getAttribute('href')
        return document.querySelector(anchorId)
      })
      const lastViewedItems = pageAnchors.filter((item) => {
        return item && item.getBoundingClientRect().top < vh / 2 - menuHeight
      })
      const lastViewedItemId =
        lastViewedItems && lastViewedItems.length ? lastViewedItems[lastViewedItems.length - 1].id : ''
      if (this.lastAnchorId !== lastViewedItemId) {
        const previousMenuEntry = menuEntries[`#${this.lastAnchorId}`]
        if (previousMenuEntry) {
          previousMenuEntry.classList.remove('adn-anchor-navigation__link--currently-viewed')
        }
        const currentMenuEntry = menuEntries[`#${lastViewedItemId}`]
        if (currentMenuEntry) {
          currentMenuEntry.classList.add('adn-anchor-navigation__link--currently-viewed')
          // Wait for the vertical scroll to finish before execute horizontal scroll
          // Only one scroll action at a time for performance reasons
          // Read more: https://github.com/rigor789/vue-scrollto#simultaneous-scrolling
          const timeout = this.isVerticalScroll ? this.verticalScrollDuration : 0
          setTimeout(() => {
            if (this.useNative) {
              currentMenuEntry?.parentElement?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
              })
            } else {
              VueScrollTo.scrollTo(currentMenuEntry.parentElement, this.horizontalScrollDuration, {
                container: this.$refs.container,
                x: true,
                y: false,
              })
            }
          }, timeout)
        }
        this.lastAnchorId = lastViewedItemId
      }
    },
  },
}
</script>

<style lang="scss">
.adn-anchor-navigation {
  z-index: 99;
  position: sticky;
  white-space: nowrap;
}

.adn-anchor-navigation__list {
  overflow-x: auto;

  /* Hide scrollbar */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: $breakpoint-sm) {
    &.container {
      padding-left: 16px;
      padding-right: 16px;
    }
  }
}

.adn-anchor-navigation__list-item {
  display: inline-flex;
  height: var(--item-height);
  line-height: var(--item-height);

  @media (min-width: $breakpoint-sm) {
    &:not(:first-of-type) {
      margin-left: 2rem;
    }

    &:not(:last-of-type) {
      margin-right: 2rem;
    }
  }

  @media (max-width: $breakpoint-sm) {
    margin-left: 0;
    margin-right: 8px;

    &:last-child {
      margin-right: 0;
    }
  }
}

.adn-anchor-navigation__link {
  border-bottom: 4px solid transparent;
  padding: 0 1rem;
  text-align: center;
  font-weight: bold;
  color: var(--color-text-light);
  transition: all 0.3s ease-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      border-color: currentcolor;
      color: var(--color-main);
    }
  }

  &:focus {
    cursor: pointer;
    border-color: currentcolor;
    color: var(--color-main);
  }
}

.adn-anchor-navigation__link--currently-viewed {
  border-color: currentcolor;
  color: var(--color-main);
}
</style>
